import * as React from 'react';
import { useState, useRef, useEffect, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import moment from 'moment';
import { NotificationItem } from '../../reducers/notifications';

interface Props {
    selectedItems: NotificationItem[];
    totalVisible: number;
    onSelectAll: () => void;
    onClearSelection: () => void;
    onBulkMarkRead: (ids: number[]) => void;
    onBulkToggleStar: (ids: number[]) => void;
    onBulkSnooze: (ids: number[], untilIso: string | null) => void;
}

function computeSnoozeUntil(preset: 'tomorrow' | 'monday' | 'nextWeek'): string {
    const m = moment();
    switch (preset) {
        case 'tomorrow':
            return m.add(1, 'day').hour(8).minute(0).second(0).millisecond(0).toISOString();
        case 'monday':
            return m.isoWeekday(8).hour(8).minute(0).second(0).millisecond(0).toISOString();
        case 'nextWeek':
            return m.add(7, 'days').hour(8).minute(0).second(0).millisecond(0).toISOString();
    }
}

/**
 * Sticky bar that appears at the top of the list once one or more rows are
 * selected. Mirrors Gmail's selection-bar pattern: count on the left, action
 * verbs in the middle, clear on the right. Disappears when the selection is
 * empty so the list reads normally.
 */
const NotificationBulkToolbar: React.FC<Props> = ({
    selectedItems,
    totalVisible,
    onSelectAll,
    onClearSelection,
    onBulkMarkRead,
    onBulkToggleStar,
    onBulkSnooze,
}) => {
    const intl = useIntl();
    const [snoozeOpen, setSnoozeOpen] = useState(false);
    const masterRef = useRef<HTMLInputElement>(null);
    const snoozeRef = useRef<HTMLDivElement>(null);

    const count = selectedItems.length;
    const allRead = selectedItems.every((i) => !!i.read_at);
    const allStarred = count > 0 && selectedItems.every((i) => i.starred);

    // Indeterminate state on the master — partial selection shows a dash.
    useEffect(() => {
        if (!masterRef.current) return;
        masterRef.current.indeterminate = count > 0 && count < totalVisible;
    }, [count, totalVisible]);

    // Close snooze submenu on outside click.
    useEffect(() => {
        if (!snoozeOpen) return;
        const handler = (e: MouseEvent) => {
            if (snoozeRef.current && !snoozeRef.current.contains(e.target as Node)) {
                setSnoozeOpen(false);
            }
        };
        document.addEventListener('mousedown', handler, true);
        return () => document.removeEventListener('mousedown', handler, true);
    }, [snoozeOpen]);

    const handleMasterClick = useCallback(() => {
        if (count > 0) {
            onClearSelection();
        } else {
            onSelectAll();
        }
    }, [count, onClearSelection, onSelectAll]);

    const handleMarkRead = useCallback(() => {
        const ids = selectedItems.filter((i) => !i.read_at).map((i) => i.id);
        if (ids.length) onBulkMarkRead(ids);
    }, [selectedItems, onBulkMarkRead]);

    const handleStarToggle = useCallback(() => {
        // If all selected are already starred, flip them all off. Otherwise,
        // flip all NOT-starred ones on. Either branch sends only items that
        // actually need to change, so the network call list is minimal.
        const ids = allStarred
            ? selectedItems.map((i) => i.id)
            : selectedItems.filter((i) => !i.starred).map((i) => i.id);
        if (ids.length) onBulkToggleStar(ids);
    }, [selectedItems, allStarred, onBulkToggleStar]);

    const handleSnoozePreset = (preset: 'tomorrow' | 'monday' | 'nextWeek') => () => {
        const ids = selectedItems.map((i) => i.id);
        onBulkSnooze(ids, computeSnoozeUntil(preset));
        setSnoozeOpen(false);
    };

    return (
        <div className="bulk-bar" role="toolbar" aria-label="Bulk actions">
            <label className="bulk-bar__master">
                <input
                    ref={masterRef}
                    type="checkbox"
                    checked={count > 0 && count === totalVisible}
                    onChange={handleMasterClick}
                    aria-label={intl.formatMessage({ id: 'notifications.bulk.selectAll', defaultMessage: 'Select all' })}
                />
            </label>

            <span className="bulk-bar__count">
                <FormattedMessage
                    id="notifications.bulk.selectedCount"
                    defaultMessage="{count, plural, one {# selected} other {# selected}}"
                    values={{ count }}
                />
            </span>

            <div className="bulk-bar__actions">
                {!allRead && (
                    <button type="button" className="bulk-bar__action" onClick={handleMarkRead}>
                        <span className="bulk-bar__icon" aria-hidden="true">✓</span>
                        <FormattedMessage id="notifications.bulk.markRead" defaultMessage="Mark read" />
                    </button>
                )}

                <button type="button" className="bulk-bar__action" onClick={handleStarToggle}>
                    <span className="bulk-bar__icon" aria-hidden="true">{allStarred ? '☆' : '★'}</span>
                    <FormattedMessage
                        id={allStarred ? 'notifications.bulk.unstarAll' : 'notifications.bulk.starAll'}
                        defaultMessage={allStarred ? 'Unmark all' : 'Mark all'}
                    />
                </button>

                <div className="bulk-bar__snooze" ref={snoozeRef}>
                    <button
                        type="button"
                        className={`bulk-bar__action ${snoozeOpen ? 'is-open' : ''}`}
                        onClick={() => setSnoozeOpen((v) => !v)}
                        aria-haspopup="menu"
                        aria-expanded={snoozeOpen}
                    >
                        <span className="bulk-bar__icon" aria-hidden="true">⏰</span>
                        <FormattedMessage id="notifications.bulk.snooze" defaultMessage="Snooze" />
                        <span className="bulk-bar__caret" aria-hidden="true">▾</span>
                    </button>
                    {snoozeOpen && (
                        <div className="bulk-bar__snooze-menu" role="menu">
                            <button type="button" role="menuitem" className="bulk-bar__snooze-item" onClick={handleSnoozePreset('tomorrow')}>
                                <FormattedMessage id="notifications.row.snoozeTomorrow" defaultMessage="Until tomorrow 8 AM" />
                            </button>
                            <button type="button" role="menuitem" className="bulk-bar__snooze-item" onClick={handleSnoozePreset('monday')}>
                                <FormattedMessage id="notifications.row.snoozeMonday" defaultMessage="Until Monday 8 AM" />
                            </button>
                            <button type="button" role="menuitem" className="bulk-bar__snooze-item" onClick={handleSnoozePreset('nextWeek')}>
                                <FormattedMessage id="notifications.row.snoozeNextWeek" defaultMessage="In a week" />
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <button
                type="button"
                className="bulk-bar__close"
                onClick={onClearSelection}
                aria-label={intl.formatMessage({ id: 'notifications.bulk.clear', defaultMessage: 'Clear selection' })}
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <line x1="6" y1="6" x2="18" y2="18" />
                    <line x1="18" y1="6" x2="6" y2="18" />
                </svg>
            </button>
        </div>
    );
};

export default NotificationBulkToolbar;
